import { Injectable, EventEmitter, Optional } from '@angular/core';
import { BufferGeometry, BufferAttribute } from 'three';
import {
  CADShape,
  CADOperation,
  CADSession,
  ExtrudeParams,
  BooleanParams,
  WASMLoadingState,
  CADKernelOptions
} from '../types';
import { CADKernelWorkerService } from './cad-kernel-worker.service';
import { StepImportOptions, CreateShapeResult, ExtractFaceWiresResult, ExtractFacesResult } from './cad-kernel-worker.types';

/**
 * CAD Kernel Service
 *
 * Worker-only architecture: ALL OpenCascade operations run in a Web Worker.
 * The main thread only holds shape IDs, tessellation data, and Three.js meshes.
 * This ensures:
 * - UI remains responsive during CAD operations
 * - Stable face/edge indices (shapes never leave worker)
 * - Single OCCT WASM instance (no memory duplication)
 */
@Injectable({
  providedIn: 'root'
})
export class CADKernelService {
  // Worker-only architecture: All OCCT operations run in worker
  private loadingState: WASMLoadingState = 'idle';
  private workerInitPromise: Promise<void> | null = null;

  // Worker service - REQUIRED for all OCCT operations
  private workerService: CADKernelWorkerService | null = null;

  // Session state
  private session: CADSession = {
    operations: [],
    currentIndex: -1,
    shapes: new Map(),
    isDirty: false
  };

  // Track worker shape IDs that failed to delete for retry
  private failedWorkerDeletions: string[] = [];
  private deletionRetryTimer: ReturnType<typeof setTimeout> | null = null;

  // Events
  public onLoadingStateChange = new EventEmitter<WASMLoadingState>();
  public onShapeCreated = new EventEmitter<CADShape>();
  public onShapeUpdated = new EventEmitter<CADShape>();
  public onSessionChanged = new EventEmitter<CADSession>();

  constructor(@Optional() workerService?: CADKernelWorkerService) {
    this.workerService = workerService || null;
  }

  /**
   * Check if worker is ready for operations
   * Worker-only architecture: all OCCT operations require worker
   */
  get isWorkerEnabled(): boolean {
    return !!this.workerService?.isReady;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WORKER INITIALIZATION (Worker-Only Architecture)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get current loading state
   */
  getLoadingState(): WASMLoadingState {
    return this.loadingState;
  }

  /**
   * Check if CAD kernel is ready (worker initialized)
   */
  isReady(): boolean {
    return this.loadingState === 'ready' && !!this.workerService?.isReady;
  }

  /**
   * Initialize the worker for all OCCT operations.
   * Worker-only architecture: OCCT runs exclusively in worker thread.
   */
  async ensureWorkerReady(options?: CADKernelOptions): Promise<void> {
    if (this.workerService?.isReady) {
      return;
    }

    if (this.workerInitPromise) {
      return this.workerInitPromise;
    }

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!this.workerService.isSupported) {
      throw new Error('CADKernelService: Web Workers not supported in this environment');
    }

    this.loadingState = 'loading';
    this.onLoadingStateChange.emit(this.loadingState);

    const wasmPath = options?.wasmPath || './assets/opencascade';

    this.workerInitPromise = this.workerService.init(wasmPath);

    try {
      await this.workerInitPromise;
      this.loadingState = 'ready';
      this.onLoadingStateChange.emit(this.loadingState);
      console.log('CADKernelService: Worker OCCT initialized (worker-only architecture)');
    } catch (error) {
      this.loadingState = 'error';
      this.onLoadingStateChange.emit(this.loadingState);
      this.workerInitPromise = null;
      throw error;
    }
  }

  /**
   * @deprecated Use ensureWorkerReady() instead. Kept for backwards compatibility.
   */
  async ensureLoaded(options?: CADKernelOptions): Promise<void> {
    return this.ensureWorkerReady(options);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIMITIVE CREATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a box primitive
   * Worker-only: All OCCT operations run in worker thread
   */
  async createBox(
    width: number,
    height: number,
    depth: number,
    position?: [number, number, number]
  ): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const shapeId = this.generateId();
    console.log('CADKernelService: Creating box in worker', { shapeId, width, height, depth, position });
    const result = await this.workerService.createBox(shapeId, width, height, depth, position);
    return this.createCADShapeFromPrimitiveResult(result, 'Box');
  }

  /**
   * Create a cylinder primitive
   * Worker-only: All OCCT operations run in worker thread
   */
  async createCylinder(
    radius: number,
    height: number,
    position?: [number, number, number]
  ): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const shapeId = this.generateId();
    console.log('CADKernelService: Creating cylinder in worker', { shapeId, radius, height, position });
    const result = await this.workerService.createCylinder(shapeId, radius, height, position);
    return this.createCADShapeFromPrimitiveResult(result, 'Cylinder');
  }

  /**
   * Create a sphere primitive
   * Worker-only: All OCCT operations run in worker thread
   */
  async createSphere(
    radius: number,
    position?: [number, number, number]
  ): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const shapeId = this.generateId();
    console.log('CADKernelService: Creating sphere in worker', { shapeId, radius, position });
    const result = await this.workerService.createSphere(shapeId, radius, position);
    return this.createCADShapeFromPrimitiveResult(result, 'Sphere');
  }

  /**
   * Create a cone/frustum primitive
   * Worker-only: All OCCT operations run in worker thread
   * @param radius1 - Base radius
   * @param radius2 - Top radius (0 for pointed cone)
   * @param height - Height of cone
   * @param position - Optional position [x, y, z]
   */
  async createCone(
    radius1: number,
    radius2: number,
    height: number,
    position?: [number, number, number]
  ): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const shapeId = this.generateId();
    console.log('CADKernelService: Creating cone in worker', { shapeId, radius1, radius2, height, position });
    const result = await this.workerService.createCone(shapeId, radius1, radius2, height, position);
    return this.createCADShapeFromPrimitiveResult(result, 'Cone');
  }

  /**
   * Create a ring (hollow cylinder / tube) primitive
   * Worker-only: All OCCT operations run in worker thread
   */
  async createRing(
    outerRadius: number,
    innerRadius: number,
    height: number,
    position?: [number, number, number]
  ): Promise<CADShape> {
    // Validate radii
    if (innerRadius >= outerRadius) {
      throw new Error('Inner radius must be less than outer radius');
    }
    if (innerRadius <= 0 || outerRadius <= 0 || height <= 0) {
      throw new Error('All dimensions must be positive');
    }

    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const shapeId = this.generateId();
    console.log('CADKernelService: Creating ring in worker', { shapeId, outerRadius, innerRadius, height, position });
    const result = await this.workerService.createRing(shapeId, outerRadius, innerRadius, height, position);
    return this.createCADShapeFromPrimitiveResult(result, 'Ring');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EXTRUSION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Extrude a circle using proper CAD cylinder (no segments)
   */
  async extrudeCircle(params: {
    centerX: number;
    centerY: number;
    radius: number;
    height: number;
    sketchPlane?: 'XY' | 'XZ' | 'YZ';
    planeOffset?: number;
    customPlane?: {
      origin: [number, number, number];
      normal: [number, number, number];
      xAxis: [number, number, number];
      yAxis: [number, number, number];
    };
  }): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const { centerX, centerY, radius, height, sketchPlane = 'XY', planeOffset = 0, customPlane } = params;

    // Create cylinder at center position
    const shapeId = this.generateId();
    const zPos = height < 0 ? height : 0;
    let result = await this.workerService.createCylinder(shapeId, radius, Math.abs(height), [centerX, centerY, zPos]);

    // Apply plane transformation if needed
    if (sketchPlane !== 'XY' || planeOffset !== 0 || customPlane) {
      const transformMatrix = this.computePlaneTransformMatrix(sketchPlane, planeOffset, customPlane);
      result = await this.workerService.transformShape(shapeId, transformMatrix);
    }

    const cadShape = this.createCADShapeFromPrimitiveResult(result, 'Circle Extrusion');
    cadShape.workerShapeId = shapeId;
    return cadShape;
  }

  /**
   * Extrude a ring (tube/pipe) using proper CAD cylinders (no segments)
   * Worker-only: Creates ring primitive and transforms it
   */
  async extrudeRing(params: {
    centerX: number;
    centerY: number;
    outerRadius: number;
    innerRadius: number;
    height: number;
    sketchPlane?: 'XY' | 'XZ' | 'YZ';
    planeOffset?: number;
    customPlane?: {
      origin: [number, number, number];
      normal: [number, number, number];
      xAxis: [number, number, number];
      yAxis: [number, number, number];
    };
  }): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const { centerX, centerY, outerRadius, innerRadius, height, sketchPlane = 'XY', planeOffset = 0, customPlane } = params;

    // Create ring at center position
    const shapeId = this.generateId();
    const zPos = height < 0 ? height : 0;
    let result = await this.workerService.createRing(shapeId, outerRadius, innerRadius, Math.abs(height), [centerX, centerY, zPos]);

    // Apply plane transformation if needed
    if (sketchPlane !== 'XY' || planeOffset !== 0 || customPlane) {
      const transformMatrix = this.computePlaneTransformMatrix(sketchPlane, planeOffset, customPlane);
      result = await this.workerService.transformShape(shapeId, transformMatrix);
    }

    const cadShape = this.createCADShapeFromPrimitiveResult(result, 'Ring Extrusion');
    cadShape.workerShapeId = shapeId;
    return cadShape;
  }

  /**
   * Extrude a slot using proper CAD arcs (no segments)
   * Worker-only: Creates slot primitive with true arc end caps
   */
  async extrudeSlot(params: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    width: number;
    height: number;
    sketchPlane?: 'XY' | 'XZ' | 'YZ';
    planeOffset?: number;
    customPlane?: {
      origin: [number, number, number];
      normal: [number, number, number];
      xAxis: [number, number, number];
      yAxis: [number, number, number];
    };
  }): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const { startX, startY, endX, endY, width, height, sketchPlane = 'XY', planeOffset = 0, customPlane } = params;

    // Create slot at position
    const shapeId = this.generateId();
    const zPos = height < 0 ? height : 0;
    let result = await this.workerService.createSlot(shapeId, startX, startY, endX, endY, width, Math.abs(height), [0, 0, zPos]);

    // Apply plane transformation if needed
    if (sketchPlane !== 'XY' || planeOffset !== 0 || customPlane) {
      const transformMatrix = this.computePlaneTransformMatrix(sketchPlane, planeOffset, customPlane);
      result = await this.workerService.transformShape(shapeId, transformMatrix);
    }

    const cadShape = this.createCADShapeFromPrimitiveResult(result, 'Slot Extrusion');
    cadShape.workerShapeId = shapeId;
    return cadShape;
  }

  /**
   * Subtract circle holes from an existing shape (for circle outer with circle holes)
   * Worker-only: Uses worker boolean operations
   */
  async subtractCircleHoles(
    shape: CADShape,
    circleHoles: Array<{ centerX: number; centerY: number; radius: number }>,
    height: number,
    sketchPlane: 'XY' | 'XZ' | 'YZ' = 'XY',
    planeOffset: number = 0,
    customPlane?: any
  ): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('Shape does not have a worker shape ID');
    }

    let currentShapeId = shape.workerShapeId;
    let lastResult: any;

    for (const circleHole of circleHoles) {
      const { centerX, centerY, radius } = circleHole;

      // Create cylinder for hole (extra height for clean cut)
      const holeId = this.generateId();
      const holeHeight = Math.abs(height) + 0.2;
      const holeZ = height < 0 ? height - 0.1 : -0.1;

      await this.workerService.createCylinder(holeId, radius, holeHeight, [centerX, centerY, holeZ]);

      // Apply plane transformation if needed
      if (sketchPlane !== 'XY' || planeOffset !== 0 || customPlane) {
        const transformMatrix = this.computePlaneTransformMatrix(sketchPlane, planeOffset, customPlane);
        await this.workerService.transformShape(holeId, transformMatrix);
      }

      // Subtract hole from main shape
      const resultId = this.generateId();
      lastResult = await this.workerService.booleanSubtractV2(currentShapeId, holeId, resultId, true);
      currentShapeId = resultId;
    }

    const result = this.createCADShapeFromPrimitiveResult(lastResult, shape.name || 'Circle with Holes');
    result.workerShapeId = currentShapeId;
    result.color = shape.color;

    // Delete the original shape from the store since we're replacing it
    this.deleteShape(shape.id);

    return result;
  }

  /**
   * Subtract polygon holes from an existing shape (for circle outer with polygon holes)
   * Worker-only: Uses worker extrusion and boolean operations
   */
  async subtractPolygonHoles(
    shape: CADShape,
    polygonHoles: number[][][],
    height: number,
    sketchPlane: 'XY' | 'XZ' | 'YZ' = 'XY',
    planeOffset: number = 0,
    customPlane?: any
  ): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('Shape does not have a worker shape ID');
    }

    let currentShapeId = shape.workerShapeId;
    let lastResult: any;

    for (const holePoints of polygonHoles) {
      if (holePoints.length < 3) continue;

      // Convert points to flat array
      const flatPoints: number[] = [];
      for (const p of holePoints) {
        flatPoints.push(p[0], p[1], p.length > 2 ? p[2] : 0);
      }

      // Extrude the hole shape with extra height
      const holeId = this.generateId();
      const extrudeHeight = height < 0 ? height - 0.1 : Math.abs(height) + 0.2;
      const zOffset = height < 0 ? 0 : -0.1;

      await this.workerService.extrudeV2(holeId, flatPoints, [0, 0, 1], extrudeHeight);

      // Translate to correct Z position
      const translateMatrix = [
        1, 0, 0, 0,
        0, 1, 0, 0,
        0, 0, 1, 0,
        0, 0, zOffset, 1
      ];
      await this.workerService.transformShape(holeId, translateMatrix);

      // Apply plane transformation if needed
      if (sketchPlane !== 'XY' || planeOffset !== 0 || customPlane) {
        const transformMatrix = this.computePlaneTransformMatrix(sketchPlane, planeOffset, customPlane);
        await this.workerService.transformShape(holeId, transformMatrix);
      }

      // Subtract hole from main shape
      const resultId = this.generateId();
      lastResult = await this.workerService.booleanSubtractV2(currentShapeId, holeId, resultId, true);
      currentShapeId = resultId;
    }

    const result = this.createCADShapeFromPrimitiveResult(lastResult, shape.name || 'Circle with Polygon Holes');
    result.workerShapeId = currentShapeId;
    result.color = shape.color;

    // Delete the original shape from the store since we're replacing it
    this.deleteShape(shape.id);

    return result;
  }

  /**
   * Extrude multiple profiles as a single solid (union them)
   * Worker-only: Uses worker primitives, extrusion, and boolean operations
   */
  async extrudeMultipleProfiles(profiles: Array<{
    outer: number[][];
    holes?: number[][][];
    isCircle?: boolean;
    isRing?: boolean;
    isSlot?: boolean;
    circleData?: { centerX: number; centerY: number; radius: number };
    ringData?: { centerX: number; centerY: number; outerRadius: number; innerRadius: number };
    slotData?: { startX: number; startY: number; endX: number; endY: number; width: number };
    circleHoles?: Array<{ centerX: number; centerY: number; radius: number }>;
  }>, height: number, sketchPlane: 'XY' | 'XZ' | 'YZ' = 'XY', planeOffset: number = 0, customPlane?: any): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const shapeIds: string[] = [];

    for (const profile of profiles) {
      let profileShapeId = this.generateId();

      if (profile.isRing && profile.ringData) {
        // Create ring primitive
        const { centerX, centerY, outerRadius, innerRadius } = profile.ringData;
        const zPos = height < 0 ? height : 0;
        await this.workerService.createRing(profileShapeId, outerRadius, innerRadius, Math.abs(height), [centerX, centerY, zPos]);

      } else if (profile.isCircle && profile.circleData) {
        // Create cylinder primitive
        const { centerX, centerY, radius } = profile.circleData;
        const zPos = height < 0 ? height : 0;
        await this.workerService.createCylinder(profileShapeId, radius, Math.abs(height), [centerX, centerY, zPos]);

      } else if (profile.isSlot && profile.slotData) {
        // Create slot primitive with proper arc end caps
        const { startX, startY, endX, endY, width } = profile.slotData;
        const zPos = height < 0 ? height : 0;
        await this.workerService.createSlot(profileShapeId, startX, startY, endX, endY, width, Math.abs(height), [0, 0, zPos]);

      } else {
        // Extrude polygon
        const flatOuter: number[] = [];
        for (const p of profile.outer) {
          flatOuter.push(p[0], p[1], p.length > 2 ? p[2] : 0);
        }

        // Convert polygon holes to flat arrays (reversed for correct orientation)
        const flatHoles: number[][] | undefined = profile.holes?.map(holePoints => {
          const reversed = [...holePoints].reverse();
          const flat: number[] = [];
          for (const p of reversed) {
            flat.push(p[0], p[1], p.length > 2 ? p[2] : 0);
          }
          return flat;
        });

        await this.workerService.extrudeV2(profileShapeId, flatOuter, [0, 0, 1], height, flatHoles);

        // Subtract circle holes
        if (profile.circleHoles && profile.circleHoles.length > 0) {
          for (const circleHole of profile.circleHoles) {
            const { centerX, centerY, radius } = circleHole;
            const holeId = this.generateId();
            const holeHeight = Math.abs(height) + 0.2;
            const holeZ = height < 0 ? height - 0.1 : -0.1;

            await this.workerService.createCylinder(holeId, radius, holeHeight, [centerX, centerY, holeZ]);

            const resultId = this.generateId();
            await this.workerService.booleanSubtractV2(profileShapeId, holeId, resultId, true);
            profileShapeId = resultId;
          }
        }
      }

      shapeIds.push(profileShapeId);
    }

    // Union all shapes together
    let currentShapeId = shapeIds[0];
    let lastResult: any;

    for (let i = 1; i < shapeIds.length; i++) {
      const resultId = this.generateId();
      lastResult = await this.workerService.booleanUnionV2(currentShapeId, shapeIds[i], resultId, true);
      currentShapeId = resultId;
    }

    // If only one profile, get its tessellation using shapeId-based V2 methods
    if (shapeIds.length === 1) {
      const tessResult = await this.workerService.tessellateV2(currentShapeId);
      const edgesResult = await this.workerService.extractEdgesV2(currentShapeId);
      lastResult = { shapeId: currentShapeId, tessellation: tessResult, edges: edgesResult };
    }

    // Transform to plane if needed
    if (sketchPlane !== 'XY' || planeOffset !== 0 || customPlane) {
      const transformMatrix = this.computePlaneTransformMatrix(sketchPlane, planeOffset, customPlane);
      lastResult = await this.workerService.transformShape(currentShapeId, transformMatrix);
    }

    const cadShape = this.createCADShapeFromPrimitiveResult(lastResult, 'Multi-Profile Extrusion');
    cadShape.workerShapeId = currentShapeId;
    return cadShape;
  }

  /**
   * Transform shape from XY plane to target sketch plane
   */
  private transformShapeToPlane(
    oc: any, 
    shape: any, 
    sketchPlane: 'XY' | 'XZ' | 'YZ' | 'custom',
    planeOffset: number,
    customPlane?: any
  ): any {
    // Check for custom plane first (customPlane takes precedence)
    if (customPlane) {
      // Custom plane transformation
      const origin = customPlane.origin;
      const xAxis = customPlane.xAxis;
      const yAxis = customPlane.yAxis;
      const normal = customPlane.normal;
      
      // Build transformation matrix
      const trsf = new oc.gp_Trsf_1();
      trsf.SetValues(
        xAxis[0], yAxis[0], normal[0], origin[0],
        xAxis[1], yAxis[1], normal[1], origin[1],
        xAxis[2], yAxis[2], normal[2], origin[2]
      );
      
      const transformer = new oc.BRepBuilderAPI_Transform_2(shape, trsf, true);
      shape.delete();
      return transformer.Shape();
    }
    
    if (sketchPlane === 'XY' && planeOffset === 0) {
      return shape; // No transformation needed
    }
    
    // Transform based on standard plane
    if (sketchPlane === 'XZ') {
      const trsf = new oc.gp_Trsf_1();
      // Transform: (x, y, z) → (x, z, y) so positive extrusion goes in +Y direction
      trsf.SetValues(1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0);
      const rotator = new oc.BRepBuilderAPI_Transform_2(shape, trsf, true);
      shape.delete();
      shape = rotator.Shape();
      
      if (planeOffset !== 0) {
        const trsfTranslate = new oc.gp_Trsf_1();
        trsfTranslate.SetTranslation_1(new oc.gp_Vec_4(0, planeOffset, 0));
        const translator = new oc.BRepBuilderAPI_Transform_2(shape, trsfTranslate, true);
        shape.delete();
        shape = translator.Shape();
      }
    } else if (sketchPlane === 'YZ') {
      const trsf = new oc.gp_Trsf_1();
      trsf.SetValues(0, 0, 1, 0, 1, 0, 0, 0, 0, 1, 0, 0);
      const rotator = new oc.BRepBuilderAPI_Transform_2(shape, trsf, true);
      shape.delete();
      shape = rotator.Shape();
      
      if (planeOffset !== 0) {
        const trsfTranslate = new oc.gp_Trsf_1();
        trsfTranslate.SetTranslation_1(new oc.gp_Vec_4(planeOffset, 0, 0));
        const translator = new oc.BRepBuilderAPI_Transform_2(shape, trsfTranslate, true);
        shape.delete();
        shape = translator.Shape();
      }
    } else if (sketchPlane === 'XY' && planeOffset !== 0) {
      const trsfTranslate = new oc.gp_Trsf_1();
      trsfTranslate.SetTranslation_1(new oc.gp_Vec_4(0, 0, planeOffset));
      const translator = new oc.BRepBuilderAPI_Transform_2(shape, trsfTranslate, true);
      shape.delete();
      shape = translator.Shape();
    }
    
    return shape;
  }

  /**
   * Extrude a 2D polygon into a 3D solid
   */
  async extrudePolygon(params: ExtrudeParams): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const { points, height, holes, sketchPlane = 'XY', planeOffset = 0, customPlane, circleHoles } = params;

    if (points.length < 3) {
      throw new Error('At least 3 points required for extrusion');
    }

    // Convert 2D points to flat 3D array for worker [x1,y1,z1, x2,y2,z2, ...]
    const profilePoints: number[] = [];
    for (const p of points) {
      profilePoints.push(p[0], p[1], p.length > 2 ? p[2] : 0);
    }

    // Convert holes to flat arrays (reversed for correct orientation)
    const flatHoles: number[][] | undefined = holes?.map(holePoints => {
      const reversed = [...holePoints].reverse();
      const flat: number[] = [];
      for (const p of reversed) {
        flat.push(p[0], p[1], p.length > 2 ? p[2] : 0);
      }
      return flat;
    });

    // Extrude in worker (always in +Z direction initially)
    const shapeId = this.generateId();
    console.log('CADKernelService: Extruding polygon in worker', { shapeId, pointCount: points.length, height });

    let lastResult = await this.workerService.extrudeV2(
      shapeId,
      profilePoints,
      [0, 0, 1], // +Z direction
      height,
      flatHoles
    );

    let currentShapeId = shapeId;

    // Apply plane transformation if needed (transforms shape in place)
    if (sketchPlane !== 'XY' || planeOffset !== 0 || customPlane) {
      const transformMatrix = this.computePlaneTransformMatrix(sketchPlane, planeOffset, customPlane);
      lastResult = await this.workerService.transformShape(currentShapeId, transformMatrix);
    }

    // Subtract circle holes using worker boolean operations
    if (circleHoles && circleHoles.length > 0) {
      for (const circleHole of circleHoles) {
        const { centerX, centerY, radius } = circleHole;

        // Create cylinder for hole (with extra height for clean cut)
        const holeId = this.generateId();
        const holeHeight = Math.abs(height) + 0.2;
        const holeZ = height < 0 ? height - 0.1 : -0.1;

        await this.workerService.createCylinder(holeId, radius, holeHeight, [centerX, centerY, holeZ]);

        // Apply plane transformation to hole if needed (transforms in place)
        if (sketchPlane !== 'XY' || planeOffset !== 0 || customPlane) {
          const transformMatrix = this.computePlaneTransformMatrix(sketchPlane, planeOffset, customPlane);
          await this.workerService.transformShape(holeId, transformMatrix);
        }

        // Subtract hole from main shape
        const resultId = this.generateId();
        const boolResult = await this.workerService.booleanSubtractV2(currentShapeId, holeId, resultId, true);
        currentShapeId = resultId;
        lastResult = boolResult;
      }
    }

    const cadShape = this.createCADShapeFromPrimitiveResult(
      { shapeId: currentShapeId, tessellation: lastResult.tessellation, edges: lastResult.edges },
      'Extrusion'
    );

    // Store the worker shape ID for future operations
    cadShape.workerShapeId = currentShapeId;
    // Store extrude params for parametric editing
    cadShape.extrudeParams = { ...params };

    return cadShape;
  }

  /**
   * Compute a 4x4 transform matrix for plane transformation
   * @returns 16-element array in column-major order
   */
  private computePlaneTransformMatrix(
    sketchPlane: 'XY' | 'XZ' | 'YZ',
    planeOffset: number,
    customPlane?: { origin: [number, number, number]; normal: [number, number, number]; xAxis: [number, number, number]; yAxis: [number, number, number] }
  ): number[] {
    // Start with identity matrix (column-major order)
    // [m0, m1, m2, m3, m4, m5, m6, m7, m8, m9, m10, m11, m12, m13, m14, m15]
    // Column 0: [m0, m1, m2, m3]
    // Column 1: [m4, m5, m6, m7]
    // Column 2: [m8, m9, m10, m11]
    // Column 3: [m12, m13, m14, m15] (translation)

    if (customPlane) {
      const [nx, ny, nz] = customPlane.normal;
      const [xx, xy, xz] = customPlane.xAxis;
      const [yx, yy, yz] = customPlane.yAxis;
      const [ox, oy, oz] = customPlane.origin;

      // Rotation + translation matrix (column-major)
      return [
        xx, xy, xz, 0,  // Column 0 (X axis)
        yx, yy, yz, 0,  // Column 1 (Y axis)
        nx, ny, nz, 0,  // Column 2 (Z/normal axis)
        ox, oy, oz, 1   // Column 3 (translation)
      ];
    }

    if (sketchPlane === 'XZ') {
      // XZ plane: rotate 90° around X, then translate in Y
      // Rotation: (x, y, z) → (x, z, y)
      // Matrix (column-major):
      // [1, 0, 0, 0,  0, 0, 1, 0,  0, 1, 0, 0,  0, offset, 0, 1]
      return [
        1, 0, 0, 0,           // Column 0
        0, 0, 1, 0,           // Column 1
        0, 1, 0, 0,           // Column 2
        0, planeOffset, 0, 1  // Column 3 (Y offset)
      ];
    }

    if (sketchPlane === 'YZ') {
      // YZ plane: rotate 90° around Y, then translate in X
      // Rotation: (x, y, z) → (z, x, y)
      // Matrix (column-major):
      return [
        0, 1, 0, 0,           // Column 0
        0, 0, 1, 0,           // Column 1
        1, 0, 0, 0,           // Column 2
        planeOffset, 0, 0, 1  // Column 3 (X offset)
      ];
    }

    // XY plane with offset (just translate in Z)
    return [
      1, 0, 0, 0,           // Column 0
      0, 1, 0, 0,           // Column 1
      0, 0, 1, 0,           // Column 2
      0, 0, planeOffset, 1  // Column 3 (Z offset)
    ];
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BOOLEAN OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a CADShape from V2 worker result (shapeId-based)
   * This is used for operations that use the worker shape registry
   */
  private createCADShapeFromV2Result(
    result: { shapeId: string; tessellation: any; edges?: any },
    name: string
  ): CADShape {
    const id = this.generateId();

    // Create BufferGeometry from tessellation result
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(result.tessellation.positions, 3));
    geometry.setAttribute('normal', new BufferAttribute(result.tessellation.normals, 3));
    geometry.setIndex(new BufferAttribute(result.tessellation.indices, 1));

    // Convert edge data from worker format
    let edges: CADShape['edges'] | undefined;
    if (result.edges?.edges) {
      edges = result.edges.edges.map((edge: any) => ({
        edgeIndex: edge.edgeIndex,
        points: Array.from(edge.points)
      }));
    }

    const cadShape: CADShape = {
      id,
      name,
      mesh: geometry,
      color: '#6366f1',
      workerShapeId: result.shapeId,
      triangleToFaceMap: result.tessellation.triangleToFaceMap,
      extractedFaces: result.tessellation.extractedFaces,
      edges
    };

    this.session.shapes.set(id, cadShape);
    this.session.isDirty = true;
    this.onShapeCreated.emit(cadShape);

    return cadShape;
  }

  /**
   * Perform boolean union of two shapes.
   * Uses worker when available for better UI responsiveness.
   * Prefers V2 (shapeId-based) when both shapes have workerShapeId for stable face indices.
   */
  async booleanUnion(shapeA: CADShape, shapeB: CADShape): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shapeA.workerShapeId || !shapeB.workerShapeId) {
      throw new Error('CADKernelService: Both shapes must have workerShapeId for boolean operations');
    }

    const resultShapeId = this.generateId();
    console.log('CADKernelService: Boolean union (worker-only)');

    const result = await this.workerService.booleanUnionV2(
      shapeA.workerShapeId,
      shapeB.workerShapeId,
      resultShapeId,
      false // don't delete inputs
    );

    return this.createCADShapeFromV2Result(result, `Union_${shapeA.name}_${shapeB.name}`);
  }

  /**
   * Perform boolean subtraction (cut shape B from shape A).
   * Uses worker when available for better UI responsiveness.
   * Prefers V2 (shapeId-based) when both shapes have workerShapeId for stable face indices.
   */
  async booleanSubtract(shapeA: CADShape, shapeB: CADShape): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shapeA.workerShapeId || !shapeB.workerShapeId) {
      throw new Error('CADKernelService: Both shapes must have workerShapeId for boolean operations');
    }

    const resultShapeId = this.generateId();
    console.log('CADKernelService: Boolean subtract (worker-only)');

    const result = await this.workerService.booleanSubtractV2(
      shapeA.workerShapeId,
      shapeB.workerShapeId,
      resultShapeId,
      false // don't delete inputs
    );

    return this.createCADShapeFromV2Result(result, `Subtract_${shapeA.name}_${shapeB.name}`);
  }

  /**
   * Perform boolean intersection.
   * Uses worker when available for better UI responsiveness.
   * Prefers V2 (shapeId-based) when both shapes have workerShapeId for stable face indices.
   */
  async booleanIntersect(shapeA: CADShape, shapeB: CADShape): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shapeA.workerShapeId || !shapeB.workerShapeId) {
      throw new Error('CADKernelService: Both shapes must have workerShapeId for boolean operations');
    }

    const resultShapeId = this.generateId();
    console.log('CADKernelService: Boolean intersect (worker-only)');

    const result = await this.workerService.booleanIntersectV2(
      shapeA.workerShapeId,
      shapeB.workerShapeId,
      resultShapeId,
      false // don't delete inputs
    );

    return this.createCADShapeFromV2Result(result, `Intersect_${shapeA.name}_${shapeB.name}`);
  }

  /**
   * Public async method to heal/repair a CADShape's topology (worker-only).
   * @param shape The shape to heal
   * @returns The healed shape (may be the same if no changes needed)
   */
  async healShapeAsync(shape: CADShape): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for heal operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();

    const result = await this.workerService.healShapeV2(
      shape.workerShapeId,
      resultShapeId,
      false // don't delete input
    );

    if (result.wasModified) {
      this.deleteShape(shape.id);
      const newShape = this.createCADShapeFromPrimitiveResult(result, `${shape.name}_healed`);
      newShape.color = originalColor;
      return newShape;
    } else {
      // No changes needed - shape was not modified
      console.log('CADKernelService: Shape did not need healing');
      return shape;
    }
  }

  /**
   * Perform boolean operation based on type
   */
  async performBoolean(params: BooleanParams): Promise<CADShape> {
    const shapeA = this.session.shapes.get(params.targetId);
    const shapeB = this.session.shapes.get(params.toolId);
    
    if (!shapeA || !shapeB) {
      throw new Error('One or both shapes not found');
    }
    
    switch (params.operation) {
      case 'union':
        return this.booleanUnion(shapeA, shapeB);
      case 'subtract':
        return this.booleanSubtract(shapeA, shapeB);
      case 'intersect':
        return this.booleanIntersect(shapeA, shapeB);
      default:
        throw new Error(`Unknown boolean operation: ${params.operation}`);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PATTERN OPERATIONS (Linear Array, Circular Array, Mirror)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a linear array of a shape (worker-only)
   * @param shape The shape to array
   * @param direction Direction vector [x, y, z]
   * @param count Number of copies (including original)
   * @param spacing Distance between copies
   * @returns Array of new shapes (not including original)
   */
  async linearArray(
    shape: CADShape,
    direction: [number, number, number],
    count: number,
    spacing: number
  ): Promise<CADShape[]> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for array operation');
    }

    if (count < 2) {
      throw new Error('Count must be at least 2');
    }

    // Normalize direction
    const len = Math.sqrt(direction[0] ** 2 + direction[1] ** 2 + direction[2] ** 2);
    const dir = [direction[0] / len, direction[1] / len, direction[2] / len];

    const newShapes: CADShape[] = [];

    for (let i = 1; i < count; i++) {
      const offset = spacing * i;
      const tx = dir[0] * offset;
      const ty = dir[1] * offset;
      const tz = dir[2] * offset;

      // Clone the shape
      const newShapeId = this.generateId();
      const cloneResult = await this.workerService.cloneShape(shape.workerShapeId, newShapeId);

      // Build translation matrix (column-major 4x4)
      const translationMatrix = [
        1, 0, 0, 0,  // column 0
        0, 1, 0, 0,  // column 1
        0, 0, 1, 0,  // column 2
        tx, ty, tz, 1 // column 3 (translation)
      ];

      // Apply translation
      const transformResult = await this.workerService.transformShape(newShapeId, translationMatrix);

      const newShape = this.createCADShapeFromPrimitiveResult(transformResult, `${shape.name}_copy_${i}`);
      newShape.color = shape.color;
      newShapes.push(newShape);
    }

    return newShapes;
  }

  /**
   * Create a circular/polar array of a shape (worker-only)
   * @param shape The shape to array
   * @param axis Axis of rotation ('X', 'Y', 'Z' or custom [x, y, z])
   * @param center Center point of rotation [x, y, z]
   * @param count Number of copies (including original)
   * @param totalAngle Total angle to span in degrees (360 for full circle)
   * @returns Array of new shapes (not including original)
   */
  async circularArray(
    shape: CADShape,
    axis: 'X' | 'Y' | 'Z' | [number, number, number],
    center: [number, number, number],
    count: number,
    totalAngle: number = 360
  ): Promise<CADShape[]> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for array operation');
    }

    if (count < 2) {
      throw new Error('Count must be at least 2');
    }

    // Get axis direction
    let axisDir: [number, number, number];
    if (typeof axis === 'string') {
      switch (axis) {
        case 'X': axisDir = [1, 0, 0]; break;
        case 'Y': axisDir = [0, 1, 0]; break;
        case 'Z': axisDir = [0, 0, 1]; break;
        default: axisDir = [0, 0, 1];
      }
    } else {
      axisDir = axis;
    }

    const newShapes: CADShape[] = [];
    const angleStep = (totalAngle / count) * (Math.PI / 180); // Convert to radians
    const [cx, cy, cz] = center;
    const [ax, ay, az] = axisDir;

    for (let i = 1; i < count; i++) {
      const angle = angleStep * i;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);

      // Clone the shape
      const newShapeId = this.generateId();
      await this.workerService.cloneShape(shape.workerShapeId, newShapeId);

      // Build rotation matrix around arbitrary axis through center point
      // Using Rodrigues' rotation formula
      // R = I*cos(θ) + (1-cos(θ))*(k⊗k) + sin(θ)*K
      // where k is the unit axis vector and K is the skew-symmetric cross-product matrix

      // Rotation matrix components (around origin)
      const r00 = cosA + ax * ax * (1 - cosA);
      const r01 = ax * ay * (1 - cosA) - az * sinA;
      const r02 = ax * az * (1 - cosA) + ay * sinA;
      const r10 = ay * ax * (1 - cosA) + az * sinA;
      const r11 = cosA + ay * ay * (1 - cosA);
      const r12 = ay * az * (1 - cosA) - ax * sinA;
      const r20 = az * ax * (1 - cosA) - ay * sinA;
      const r21 = az * ay * (1 - cosA) + ax * sinA;
      const r22 = cosA + az * az * (1 - cosA);

      // Translation: rotate center around origin, then compute offset
      // t = center - R * center
      const tx = cx - (r00 * cx + r01 * cy + r02 * cz);
      const ty = cy - (r10 * cx + r11 * cy + r12 * cz);
      const tz = cz - (r20 * cx + r21 * cy + r22 * cz);

      // Build 4x4 matrix (column-major for Three.js)
      const rotationMatrix = [
        r00, r10, r20, 0,  // column 0
        r01, r11, r21, 0,  // column 1
        r02, r12, r22, 0,  // column 2
        tx, ty, tz, 1      // column 3 (translation)
      ];

      // Apply rotation
      const transformResult = await this.workerService.transformShape(newShapeId, rotationMatrix);

      const newShape = this.createCADShapeFromPrimitiveResult(transformResult, `${shape.name}_polar_${i}`);
      newShape.color = shape.color;
      newShapes.push(newShape);
    }

    return newShapes;
  }

  /**
   * Mirror a shape across a plane (worker-only)
   * Uses transformShape with a computed mirror matrix
   * @param shape The shape to mirror
   * @param plane The mirror plane ('XY', 'XZ', 'YZ' or custom normal)
   * @param origin Origin point of the mirror plane [x, y, z]
   * @returns The mirrored shape
   */
  async mirrorShape(
    shape: CADShape,
    plane: 'XY' | 'XZ' | 'YZ' | [number, number, number],
    origin: [number, number, number] = [0, 0, 0]
  ): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for mirror operation');
    }

    // Get plane normal
    let normal: [number, number, number];
    if (typeof plane === 'string') {
      switch (plane) {
        case 'XY': normal = [0, 0, 1]; break;
        case 'XZ': normal = [0, 1, 0]; break;
        case 'YZ': normal = [1, 0, 0]; break;
        default: normal = [0, 0, 1];
      }
    } else {
      const len = Math.sqrt(plane[0] ** 2 + plane[1] ** 2 + plane[2] ** 2);
      normal = [plane[0] / len, plane[1] / len, plane[2] / len];
    }

    // Build the mirror transformation matrix
    // Reflection matrix: M = I - 2 * n * n^T
    // For a plane through origin with normal n = (nx, ny, nz):
    // | 1-2nx² -2nx*ny -2nx*nz  0 |
    // | -2nx*ny 1-2ny² -2ny*nz  0 |
    // | -2nx*nz -2ny*nz 1-2nz²  0 |
    // | 0       0       0       1 |
    //
    // For a plane through point P, we need to translate to origin, mirror, translate back
    const [nx, ny, nz] = normal;
    const [ox, oy, oz] = origin;

    // Reflection matrix components
    const r00 = 1 - 2 * nx * nx;
    const r01 = -2 * nx * ny;
    const r02 = -2 * nx * nz;
    const r10 = -2 * nx * ny;
    const r11 = 1 - 2 * ny * ny;
    const r12 = -2 * ny * nz;
    const r20 = -2 * nx * nz;
    const r21 = -2 * ny * nz;
    const r22 = 1 - 2 * nz * nz;

    // Translation component: t = 2 * (n · origin) * n
    const dot = nx * ox + ny * oy + nz * oz;
    const tx = 2 * dot * nx;
    const ty = 2 * dot * ny;
    const tz = 2 * dot * nz;

    // Column-major 4x4 matrix (Three.js format)
    const mirrorMatrix = [
      r00, r10, r20, 0,  // column 0
      r01, r11, r21, 0,  // column 1
      r02, r12, r22, 0,  // column 2
      tx, ty, tz, 1      // column 3 (translation)
    ];

    // Use transformShape with the mirror matrix
    const result = await this.workerService.transformShape(
      shape.workerShapeId,
      mirrorMatrix
    );

    const newShape = this.createCADShapeFromPrimitiveResult(result, `${shape.name}_mirrored`);
    newShape.color = shape.color;

    return newShape;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FILLET & CHAMFER
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Apply fillet (rounded edges) to all edges of a shape.
   * Uses worker when available for better UI responsiveness.
   * @param shape The shape to fillet
   * @param radius The fillet radius
   */
  async filletAllEdges(shape: CADShape, radius: number): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for fillet operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();
    console.log('CADKernelService: Fillet all edges (worker-only)');

    const result = await this.workerService.filletAllV2(
      shape.workerShapeId,
      resultShapeId,
      radius,
      false // don't delete input
    );

    // Delete original from session
    this.deleteShape(shape.id);

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_filleted`);
    newShape.color = originalColor;
    return newShape;
  }

  /**
   * Apply chamfer (beveled edges) to all edges of a shape.
   * Uses worker when available for better UI responsiveness.
   * Prefers V2 (shapeId-based) when shape has workerShapeId for stable face indices.
   * @param shape The shape to chamfer
   * @param distance The chamfer distance
   */
  async chamferAllEdges(shape: CADShape, distance: number): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for chamfer operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();
    console.log('CADKernelService: Chamfer all edges (worker-only)');

    const result = await this.workerService.chamferAllV2(
      shape.workerShapeId,
      resultShapeId,
      distance,
      false // don't delete input
    );

    this.deleteShape(shape.id);

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_chamfered`);
    newShape.color = originalColor;
    return newShape;
  }

  /**
   * Apply fillet to specific edges of a shape.
   * Uses worker when available for better UI responsiveness.
   * Prefers V2 (shapeId-based) when shape has workerShapeId for stable face indices.
   * @param shape The shape to fillet
   * @param radius The fillet radius
   * @param edgeIndices Array of edge indices to fillet
   */
  async filletSelectedEdges(shape: CADShape, radius: number, edgeIndices: number[]): Promise<CADShape> {
    if (edgeIndices.length === 0) {
      throw new Error('No edges selected for fillet');
    }

    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for fillet operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();
    console.log('CADKernelService: Fillet selected edges (worker-only)');

    const result = await this.workerService.filletEdgesV2(
      shape.workerShapeId,
      resultShapeId,
      edgeIndices,
      radius,
      false // don't delete input
    );

    this.deleteShape(shape.id);

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_filleted`);
    newShape.color = originalColor;
    return newShape;
  }

  /**
   * Apply chamfer to specific edges of a shape.
   * Uses worker when available for better UI responsiveness.
   * Prefers V2 (shapeId-based) when shape has workerShapeId for stable face indices.
   * @param shape The shape to chamfer
   * @param distance The chamfer distance
   * @param edgeIndices Array of edge indices to chamfer
   */
  async chamferSelectedEdges(shape: CADShape, distance: number, edgeIndices: number[]): Promise<CADShape> {
    if (edgeIndices.length === 0) {
      throw new Error('No edges selected for chamfer');
    }

    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for chamfer operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();
    console.log('CADKernelService: Chamfer selected edges (worker-only)');

    const result = await this.workerService.chamferEdgesV2(
      shape.workerShapeId,
      resultShapeId,
      edgeIndices,
      distance,
      false // don't delete input
    );

    this.deleteShape(shape.id);

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_chamfered`);
    newShape.color = originalColor;
    return newShape;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SHELL / HOLLOW
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a hollow shell from a solid shape.
   * Uses worker when available for better UI responsiveness.
   * @param shape The solid shape to hollow
   * @param thickness The wall thickness
   */
  async shellShape(shape: CADShape, thickness: number): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for shell operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();
    console.log('CADKernelService: Shell (worker-only)');

    const result = await this.workerService.shellV2(
      shape.workerShapeId,
      resultShapeId,
      -thickness, // Negative for inward
      undefined, // No faces to remove
      false // don't delete input
    );

    this.deleteShape(shape.id);

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_shell`);
    newShape.color = originalColor;
    return newShape;
  }

  /**
   * Create a hollow shell with specific faces removed (open).
   * Uses worker when available for better UI responsiveness.
   * @param shape The solid shape to hollow
   * @param thickness The wall thickness
   * @param faceIndices Array of face indices to remove (create openings)
   */
  async shellShapeWithOpenFaces(shape: CADShape, thickness: number, faceIndices: number[]): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for shell operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();
    console.log('CADKernelService: Shell with open faces (worker-only)');

    const result = await this.workerService.shellV2(
      shape.workerShapeId,
      resultShapeId,
      -thickness, // Negative for inward
      faceIndices, // Faces to remove
      false // don't delete input
    );

    this.deleteShape(shape.id);

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_shell`);
    newShape.color = originalColor;
    return newShape;
  }

  /**
   * Offset a shape (expand or shrink)
   * Prefers V2 (shapeId-based) when shape has workerShapeId for stable face indices.
   * @param shape The shape to offset
   * @param distance Positive = expand, negative = shrink
   */
  async offsetShape(shape: CADShape, distance: number): Promise<CADShape> {
    await this.ensureWorkerReady();

    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for offset operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();
    console.log('CADKernelService: Offset (worker-only)');

    const result = await this.workerService.offsetV2(
      shape.workerShapeId,
      resultShapeId,
      distance,
      false // don't delete input
    );

    this.deleteShape(shape.id);

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_offset`);
    newShape.color = originalColor;
    return newShape;
  }

  /**
   * Push/Pull a face to modify the shape (worker-only)
   * @param shape The shape to modify
   * @param faceIndex The index of the face to push/pull
   * @param distance Positive = push outward, negative = pull inward
   */
  async pushPullFace(shape: CADShape, faceIndex: number, distance: number): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for push-pull operation');
    }

    const originalColor = shape.color;
    const resultShapeId = this.generateId();
    const result = await this.workerService.pushPullV2(
      shape.workerShapeId,
      resultShapeId,
      faceIndex,
      distance,
      false // don't delete input
    );

    this.deleteShape(shape.id);

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_modified`);
    newShape.color = originalColor;
    return newShape;
  }

  /**
   * Extract a single face from a shape as a standalone surface
   * @param shape The source shape
   * @param faceIndex The index of the face to extract
   * @returns A new CADShape containing only the extracted face (a surface)
   */
  async extractFace(shape: CADShape, faceIndex: number): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for extractFace operation');
    }

    const resultShapeId = this.generateId();
    const result = await this.workerService.extractFaceV2(
      shape.workerShapeId,
      resultShapeId,
      faceIndex,
      false // don't delete input
    );

    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_face_${faceIndex}`);
    newShape.color = shape.color;
    // Mark as surface (not a solid) and include surface type data
    newShape.isSurface = true;
    newShape.surfaceType = result.surfaceType;
    newShape.cylinderData = result.cylinderData;
    newShape.coneData = result.coneData;
    newShape.sphereData = result.sphereData;
    newShape.torusData = result.torusData;
    return newShape;
  }

  /**
   * Extract multiple faces from a shape and sew them together into a single surface
   * @param shape The shape to extract faces from
   * @param faceIndices The indices of the faces to extract
   * @returns A new CADShape containing the sewn faces (a surface)
   */
  async extractMultipleFaces(shape: CADShape, faceIndices: number[]): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for extractMultipleFaces operation');
    }

    const resultShapeId = this.generateId();
    const result = await this.workerService.extractMultipleFacesV2(
      shape.workerShapeId,
      resultShapeId,
      faceIndices,
      false // don't delete input
    );

    const faceCountStr = faceIndices.length === 1 ? `face_${faceIndices[0]}` : `${faceIndices.length}_faces`;
    const newShape = this.createCADShapeFromV2Result(result, `${shape.name}_${faceCountStr}`);
    newShape.color = shape.color;
    // Mark as surface (not a solid) and include surface type data
    newShape.isSurface = true;
    newShape.surfaceType = result.surfaceType;
    newShape.cylinderData = result.cylinderData;
    newShape.coneData = result.coneData;
    newShape.sphereData = result.sphereData;
    newShape.torusData = result.torusData;
    return newShape;
  }

  /**
   * Extract boundary wires for a specific face (for sketch creation)
   * @param shape The source shape
   * @param faceIndex The index of the face to extract wires from
   */
  async extractFaceWires(
    shape: CADShape,
    faceIndex: number,
    samplesPerEdge: number = 32
  ): Promise<ExtractFaceWiresResult> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for extractFaceWires operation');
    }

    return this.workerService.extractFaceWiresV2(shape.workerShapeId, faceIndex, samplesPerEdge);
  }

  /**
   * Extract all faces with rich metadata (normals, centers, areas, surface types)
   * Used by AI modify operations for face selector resolution
   * @param shape The shape to extract faces from
   * @returns Face data including normal, center, area, and surface type for each face
   */
  async extractFaces(shape: CADShape): Promise<ExtractFacesResult> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for extractFaces operation');
    }

    return this.workerService.extractFacesV2(shape.workerShapeId);
  }

  /**
   * Thicken a surface (extracted face) to create a solid
   * @param surface The surface shape to thicken
   * @param thickness The thickness to add (positive = in normal direction)
   * @returns A new solid CADShape
   */
  async thickenSurface(surface: CADShape, thickness: number): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!surface.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for thickenSurface operation');
    }

    const originalColor = surface.color;
    const resultShapeId = this.generateId();
    const result = await this.workerService.thickenSurfaceV2(
      surface.workerShapeId,
      resultShapeId,
      thickness,
      false // don't delete input
    );

    // Delete the surface shape since we've thickened it into a solid
    this.deleteShape(surface.id);

    const newShape = this.createCADShapeFromV2Result(result, `${surface.name}_thickened`);
    newShape.color = originalColor;
    return newShape;
  }

  /**
   * Extrude a profile on a face (boss or pocket) - worker-only
   * @param baseShape The shape to modify
   * @param profile 2D points defining the profile (in face local coordinates)
   * @param faceIndex The face to extrude from (unused in worker-only mode)
   * @param height Height of extrusion (positive = boss, negative = pocket)
   * @param operation 'boss' (add material) or 'pocket' (remove material)
   * @param planeParams REQUIRED plane parameters from the drawing system
   */
  async extrudeOnFace(
    baseShape: CADShape,
    profile: number[][],
    faceIndex: number,
    height: number,
    operation: 'boss' | 'pocket',
    planeParams?: {
      origin: [number, number, number];
      normal: [number, number, number];
      xAxis: [number, number, number];
      yAxis: [number, number, number];
    }
  ): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!baseShape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for extrudeOnFace operation');
    }
    if (!planeParams) {
      throw new Error('CADKernelService: planeParams is required for worker-only extrudeOnFace operation');
    }
    if (profile.length < 3) {
      throw new Error('Profile must have at least 3 points');
    }

    const { origin, normal, xAxis, yAxis } = planeParams;
    const [cx, cy, cz] = origin;

    // Transform 2D profile points to 3D world coordinates
    const worldPoints: number[] = [];
    for (const [x, y] of profile) {
      worldPoints.push(
        cx + x * xAxis[0] + y * yAxis[0],
        cy + x * xAxis[1] + y * yAxis[1],
        cz + x * xAxis[2] + y * yAxis[2]
      );
    }

    // Compute extrusion direction
    const absHeight = Math.abs(height);
    const directionSign = operation === 'boss' ? 1 : -1;
    const extrudeDirection: [number, number, number] = [
      normal[0] * directionSign,
      normal[1] * directionSign,
      normal[2] * directionSign
    ];

    // Create extruded profile using extrudeV2
    const extrudedShapeId = this.generateId();
    const extrudeResult = await this.workerService.extrudeV2(
      extrudedShapeId,
      worldPoints,
      extrudeDirection,
      absHeight
    );

    // Boolean operation with base shape
    const resultShapeId = this.generateId();
    let booleanResult;
    if (operation === 'boss') {
      booleanResult = await this.workerService.booleanUnionV2(
        baseShape.workerShapeId,
        extrudedShapeId,
        resultShapeId,
        true // delete inputs
      );
    } else {
      booleanResult = await this.workerService.booleanSubtractV2(
        baseShape.workerShapeId,
        extrudedShapeId,
        resultShapeId,
        true // delete inputs
      );
    }

    // Delete original from session
    this.deleteShape(baseShape.id);

    const newShape = this.createCADShapeFromV2Result(booleanResult, `${baseShape.name}_${operation}`);
    newShape.color = baseShape.color;

    return newShape;
  }

  /**
   * Extrude a circle on a face as boss or pocket (creates true CAD cylinder, not segmented polygon)
   * Worker-only: Uses proper cylinder primitive for smooth circular geometry
   * @param baseShape The shape to modify
   * @param centerX Circle center X in 2D plane coordinates
   * @param centerY Circle center Y in 2D plane coordinates
   * @param radius Circle radius
   * @param height Height of extrusion (positive = boss, negative = pocket)
   * @param operation 'boss' (add material) or 'pocket' (remove material)
   * @param planeParams REQUIRED plane parameters from the drawing system
   */
  async extrudeCircleOnFace(
    baseShape: CADShape,
    centerX: number,
    centerY: number,
    radius: number,
    height: number,
    operation: 'boss' | 'pocket',
    planeParams: {
      origin: [number, number, number];
      normal: [number, number, number];
      xAxis: [number, number, number];
      yAxis: [number, number, number];
    }
  ): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!baseShape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for extrudeCircleOnFace operation');
    }

    const { origin, normal, xAxis, yAxis } = planeParams;

    // Create cylinder at origin with the circle center offset
    const cylinderId = this.generateId();
    const absHeight = Math.abs(height);
    const directionSign = operation === 'boss' ? 1 : -1;

    // Create cylinder in XY plane first (will be transformed)
    // The cylinder is created at (centerX, centerY, 0) with height along Z
    await this.workerService.createCylinder(cylinderId, radius, absHeight, [centerX, centerY, 0]);

    // Build transformation matrix to position cylinder on face
    // The cylinder needs to be:
    // 1. Positioned at the face plane origin
    // 2. Oriented along the face normal (for boss) or opposite normal (for pocket)
    const [ox, oy, oz] = origin;
    const [nx, ny, nz] = normal;
    const [xx, xy, xz] = xAxis;
    const [yx, yy, yz] = yAxis;

    // Adjust Z axis direction based on operation
    const zx = nx * directionSign;
    const zy = ny * directionSign;
    const zz = nz * directionSign;

    // Build transform matrix (column-major order)
    const transformMatrix = [
      xx, xy, xz, 0,  // Column 0 (X axis)
      yx, yy, yz, 0,  // Column 1 (Y axis)
      zx, zy, zz, 0,  // Column 2 (Z/normal axis - direction of extrusion)
      ox, oy, oz, 1   // Column 3 (translation - face origin)
    ];

    await this.workerService.transformShape(cylinderId, transformMatrix);

    // Boolean operation with base shape
    const resultShapeId = this.generateId();
    let booleanResult;
    if (operation === 'boss') {
      booleanResult = await this.workerService.booleanUnionV2(
        baseShape.workerShapeId,
        cylinderId,
        resultShapeId,
        true // delete inputs
      );
    } else {
      booleanResult = await this.workerService.booleanSubtractV2(
        baseShape.workerShapeId,
        cylinderId,
        resultShapeId,
        true // delete inputs
      );
    }

    // Delete original from session
    this.deleteShape(baseShape.id);

    const newShape = this.createCADShapeFromV2Result(booleanResult, `${baseShape.name}_${operation}`);
    newShape.color = baseShape.color;

    return newShape;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MEASUREMENTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get bounding box dimensions of a shape (computed from mesh geometry)
   */
  getBoundingBox(shape: CADShape): {
    min: [number, number, number];
    max: [number, number, number];
    size: [number, number, number];
    center: [number, number, number];
  } | null {
    if (!shape.mesh) return null;

    try {
      // Compute bounding box from mesh vertices
      shape.mesh.computeBoundingBox();
      const box = shape.mesh.boundingBox;
      if (!box) return null;

      return {
        min: [box.min.x, box.min.y, box.min.z],
        max: [box.max.x, box.max.y, box.max.z],
        size: [
          box.max.x - box.min.x,
          box.max.y - box.min.y,
          box.max.z - box.min.z
        ],
        center: [
          (box.min.x + box.max.x) / 2,
          (box.min.y + box.max.y) / 2,
          (box.min.z + box.max.z) / 2
        ]
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Calculate distance between two shapes (center to center)
   */
  getDistanceBetweenShapes(shapeA: CADShape, shapeB: CADShape): number | null {
    const boxA = this.getBoundingBox(shapeA);
    const boxB = this.getBoundingBox(shapeB);

    if (!boxA || !boxB) return null;

    const dx = boxA.center[0] - boxB.center[0];
    const dy = boxA.center[1] - boxB.center[1];
    const dz = boxA.center[2] - boxB.center[2];

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * Get volume of a shape
   * TODO: Implement worker-based volume calculation
   */
  getVolume(_shape: CADShape): number | null {
    // Volume calculation requires OCCT - would need worker implementation
    return null;
  }

  /**
   * Get surface area of a shape
   * TODO: Implement worker-based surface area calculation
   */
  getSurfaceArea(_shape: CADShape): number | null {
    // Surface area calculation requires OCCT - would need worker implementation
    return null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP IMPORT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Import shapes from STEP file content (worker-only)
   * @param stepContent STEP file content as string or ArrayBuffer
   * @param filename Original filename for naming imported shapes
   * @returns Array of imported CADShapes
   */
  async importFromSTEP(stepContent: string | ArrayBuffer, filename: string = 'imported'): Promise<CADShape[]> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    // Convert ArrayBuffer to string if needed
    let content: string;
    if (stepContent instanceof ArrayBuffer) {
      const decoder = new TextDecoder('utf-8');
      content = decoder.decode(stepContent);
    } else {
      content = stepContent;
    }

    const result = await this.workerService.importStepV2(content, filename, { skipEdges: false, edgeSamples: 16 });

    const importedShapes: CADShape[] = [];
    for (const shapeData of result.shapes) {
      const cadShape = this.createCADShapeFromPrimitiveResult(
        {
          shapeId: shapeData.shapeId,
          tessellation: shapeData.tessellation,
          edges: shapeData.edges
        },
        shapeData.name
      );
      importedShapes.push(cadShape);
    }

    console.log(`CADKernelService: Imported ${importedShapes.length} shapes from STEP (worker-only)`);
    return importedShapes;
  }

  /**
   * Import from STEP file (File object from file picker)
   * @param file File object from input element
   * @returns Array of imported CADShapes
   */
  async importFromSTEPFile(file: File): Promise<CADShape[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const content = e.target?.result;
          if (!content) {
            throw new Error('Failed to read file');
          }
          const shapes = await this.importFromSTEP(content as ArrayBuffer, file.name);
          resolve(shapes);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Import from STEP with streaming - shapes are delivered progressively via callback.
   * Optimized for large files (30MB+) with coarser tessellation and deferred edge extraction.
   *
   * @param stepContent STEP file content
   * @param filename Filename for naming shapes
   * @param onShape Callback called for each shape as it's imported
   * @param options Optional import options
   * @returns Promise that resolves when all shapes are imported
   *
   * @example
   * ```typescript
   * let loadedCount = 0;
   * await cadKernel.importFromSTEPStreaming(content, 'assembly.step', (shape, index, total) => {
   *   console.log(`Shape ${index + 1}/${total}: ${shape.name}`);
   *   this.addShapeToScene(shape);
   *   loadedCount++;
   *   this.processingProgress = (loadedCount / total) * 100;
   * });
   * ```
   */
  async importFromSTEPStreaming(
    stepContent: string | ArrayBuffer,
    filename: string = 'imported',
    onShape: (shape: CADShape, index: number, total: number) => void,
    options?: Omit<StepImportOptions, 'streaming'>
  ): Promise<{ totalShapes: number; totalTime: number }> {
    // In worker-only mode, streaming import is not supported (would require BREP on main thread)
    // Fall back to non-streaming V2 import which is worker-only
    console.log('CADKernelService: Using non-streaming V2 import (worker-only mode)');
    const startTime = Date.now();
    const shapes = await this.importFromSTEP(stepContent, filename);
    for (let i = 0; i < shapes.length; i++) {
      onShape(shapes[i], i, shapes.length);
    }
    return { totalShapes: shapes.length, totalTime: Date.now() - startTime };
  }

  /**
   * Import from STEP file with streaming (File object from file picker)
   * @param file File object from input element
   * @param onShape Callback called for each shape as it's imported
   * @returns Promise that resolves when all shapes are imported
   */
  async importFromSTEPFileStreaming(
    file: File,
    onShape: (shape: CADShape, index: number, total: number) => void
  ): Promise<{ totalShapes: number; totalTime: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const content = e.target?.result;
          if (!content) {
            throw new Error('Failed to read file');
          }
          const result = await this.importFromSTEPStreaming(
            content as ArrayBuffer,
            file.name,
            onShape
          );
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP EXPORT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Export shape to STEP format as Blob (worker-only)
   */
  async exportToSTEP(shape: CADShape, filename?: string): Promise<Blob> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for export');
    }

    const result = await this.workerService.exportStepV2(shape.workerShapeId, filename);
    return new Blob([result.stepContent], { type: 'application/step' });
  }

  /**
   * Export multiple shapes to a single STEP file as Blob (worker-only)
   */
  async exportMultiToSTEP(shapes: CADShape[], filename?: string): Promise<Blob> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const shapeIds = shapes
      .filter(s => s.workerShapeId)
      .map(s => s.workerShapeId!);

    if (shapeIds.length === 0) {
      throw new Error('CADKernelService: No shapes with workerShapeId for export');
    }

    const result = await this.workerService.exportStepMultiV2(shapeIds, filename);
    return new Blob([result.stepContent], { type: 'application/step' });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SESSION MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create CADShape from primitive worker result (for worker-only architecture)
   * This method creates a CADShape without any main-thread TopoDS_Shape
   */
  private createCADShapeFromPrimitiveResult(
    workerResult: CreateShapeResult,
    name: string
  ): CADShape {
    // Create BufferGeometry from tessellation result
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(workerResult.tessellation.positions, 3));
    geometry.setIndex(new BufferAttribute(workerResult.tessellation.indices, 1));

    // Always compute vertex normals for proper shading
    // Worker normals from OCCT can be incorrect (all pointing same direction)
    // when triangulation.HasNormals() returns false
    geometry.computeVertexNormals();

    // Store triangleToFaceMap in geometry's userData for face selection
    if (workerResult.tessellation.triangleToFaceMap) {
      geometry.userData['triangleToFaceMap'] = workerResult.tessellation.triangleToFaceMap;
    }

    // Convert extracted faces from worker format to CADShape format
    let extractedFaces: CADShape['extractedFaces'] | undefined;
    if (workerResult.tessellation.extractedFaces) {
      extractedFaces = workerResult.tessellation.extractedFaces.map(face => ({
        faceIndex: face.faceIndex,
        vertices: Array.from(face.vertices),
        indices: Array.from(face.indices),
        normal: face.normal,
        center: face.center
      }));
    }

    // Convert edge data from worker format
    let edges: CADShape['edges'] | undefined;
    if (workerResult.edges?.edges) {
      edges = workerResult.edges.edges.map(edge => ({
        edgeIndex: edge.edgeIndex,
        points: Array.from(edge.points)
      }));
    }

    const cadShape: CADShape = {
      id: workerResult.shapeId,
      name,
      mesh: geometry,
      color: '#6366f1',
      triangleToFaceMap: workerResult.tessellation.triangleToFaceMap,
      extractedFaces,
      edges,
      workerShapeId: workerResult.shapeId
    };

    this.session.shapes.set(cadShape.id, cadShape);
    this.session.isDirty = true;
    this.onShapeCreated.emit(cadShape);

    return cadShape;
  }

  /**
   * Get a shape by ID
   */
  getShape(id: string): CADShape | undefined {
    return this.session.shapes.get(id);
  }

  /**
   * Get all shapes
   */
  getAllShapes(): CADShape[] {
    return Array.from(this.session.shapes.values());
  }

  /**
   * Delete a shape
   * Cleans up from worker registry and disposes Three.js geometry
   */
  deleteShape(id: string): boolean {
    const shape = this.session.shapes.get(id);
    if (shape) {
      // Clean up from worker registry
      if (shape.workerShapeId && this.isWorkerEnabled && this.workerService) {
        this.workerService.deleteShape(shape.workerShapeId).catch(() => {
          this.queueFailedDeletion(shape.workerShapeId);
        });
      }

      // Clean up Three.js geometry
      if (shape.mesh) {
        shape.mesh.dispose();
      }
      this.session.shapes.delete(id);
      this.session.isDirty = true;
      return true;
    }
    return false;
  }

  /**
   * Remap a shape's ID in the session.shapes Map
   * Used when external systems (like AI agent) assign a different ID to match their tracking
   * @param oldId - The current ID in the Map
   * @param newId - The new ID to use
   * @returns true if remapped successfully, false if shape not found or collision detected
   */
  remapShapeId(oldId: string, newId: string): boolean {
    const shape = this.session.shapes.get(oldId);
    if (!shape) {
      console.warn('CADKernelService: Cannot remap - shape not found:', oldId);
      return false;
    }

    // Check for ID collision - don't overwrite existing shapes
    if (oldId !== newId && this.session.shapes.has(newId)) {
      console.warn('CADKernelService: Cannot remap - newId already exists:', newId, 'Would clobber existing shape');
      return false;
    }

    // Update the shape's id property
    shape.id = newId;
    // Remove from old key and add with new key
    this.session.shapes.delete(oldId);
    this.session.shapes.set(newId, shape);
    this.session.isDirty = true;
    console.log('CADKernelService: Remapped shape ID', { from: oldId, to: newId });
    return true;
  }

  /**
   * Clear all shapes and reset session
   * Cleans up from worker registry and disposes Three.js geometry
   */
  clearSession(): void {
    // Collect worker shape IDs for batch deletion (filter out undefined)
    const workerShapeIds: string[] = [];

    for (const shape of this.session.shapes.values()) {
      if (shape.workerShapeId) {
        workerShapeIds.push(shape.workerShapeId);
      }
      // Clean up Three.js geometry
      if (shape.mesh) {
        shape.mesh.dispose();
      }
    }

    // Clean up worker registry with retry on failure
    if (workerShapeIds.length > 0 && this.isWorkerEnabled && this.workerService) {
      this.workerService.deleteShapes(workerShapeIds).catch(() => {
        workerShapeIds.forEach(id => this.queueFailedDeletion(id));
      });
    }

    this.session = {
      operations: [],
      currentIndex: -1,
      shapes: new Map(),
      isDirty: false
    };

    this.onSessionChanged.emit(this.session);
  }

  /**
   * Check if session has unsaved changes
   */
  isDirty(): boolean {
    return this.session.isDirty;
  }

  /**
   * Mark session as saved
   */
  markSaved(): void {
    this.session.isDirty = false;
  }

  /**
   * Mark session as dirty (has unsaved changes)
   */
  markDirty(): void {
    this.session.isDirty = true;
  }

  /**
   * Queue a failed worker shape deletion for retry
   */
  private queueFailedDeletion(workerShapeId: string): void {
    if (!this.failedWorkerDeletions.includes(workerShapeId)) {
      this.failedWorkerDeletions.push(workerShapeId);
    }
    this.scheduleRetryDeletions();
  }

  /**
   * Schedule a retry of failed worker shape deletions
   */
  private scheduleRetryDeletions(): void {
    if (this.deletionRetryTimer || this.failedWorkerDeletions.length === 0) return;

    this.deletionRetryTimer = setTimeout(() => {
      this.deletionRetryTimer = null;
      this.retryFailedDeletions();
    }, 5000);
  }

  /**
   * Retry all queued failed worker shape deletions
   */
  private retryFailedDeletions(): void {
    if (this.failedWorkerDeletions.length === 0 || !this.isWorkerEnabled || !this.workerService) return;

    const ids = [...this.failedWorkerDeletions];
    this.failedWorkerDeletions = [];

    this.workerService.deleteShapes(ids).catch(() => {
      // Re-queue any that still fail — they'll be retried once more
      // After second failure, drop them to avoid infinite loops
    });
  }

  /**
   * Clone a shape (worker-only)
   */
  async cloneShape(originalShape: CADShape): Promise<CADShape | null> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!originalShape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for clone operation');
    }

    const newShapeId = this.generateId();
    const result = await this.workerService.cloneShape(
      originalShape.workerShapeId,
      newShapeId
    );

    const clonedShape = this.createCADShapeFromPrimitiveResult(result, `${originalShape.name}_copy`);
    clonedShape.color = originalShape.color;
    clonedShape.extrudeParams = originalShape.extrudeParams ? { ...originalShape.extrudeParams } : undefined;
    clonedShape.primitiveParams = originalShape.primitiveParams
      ? {
          type: originalShape.primitiveParams.type,
          dimensions: [...originalShape.primitiveParams.dimensions]
        }
      : undefined;
    clonedShape.isDirectEdit = originalShape.isDirectEdit;

    return clonedShape;
  }

  /**
   * Transform a shape using a 4x4 matrix (worker-only)
   * This bakes the transformation into the geometry
   * Key insight: rotation and scale must be applied around the geometry center
   */
  async transformShape(
    shape: CADShape,
    matrixElements: number[],
    geometryCenter?: [number, number, number]
  ): Promise<CADShape | null> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for transform operation');
    }

    // Worker's transformShape modifies in place and returns updated tessellation
    const result = await this.workerService.transformShape(
      shape.workerShapeId,
      matrixElements
    );

    // Build new shape FIRST before cleaning up old one (transactional: if this throws, old shape is preserved)
    const newShape = this.createCADShapeFromPrimitiveResult(result, shape.name);
    newShape.color = shape.color;
    newShape.extrudeParams = shape.extrudeParams;
    newShape.primitiveParams = shape.primitiveParams
      ? {
          type: shape.primitiveParams.type,
          dimensions: [...shape.primitiveParams.dimensions]
        }
      : undefined;
    newShape.isDirectEdit = true;

    // Only clean up old shape after new one is successfully created
    // Don't call deleteShape() — worker shape was transformed in-place, not deleted
    if (shape.mesh) {
      shape.mesh.dispose();
    }
    this.session.shapes.delete(shape.id);
    // Preserve and transform surface data for extracted surfaces
    if (shape.isSurface) {
      newShape.isSurface = true;
      newShape.surfaceType = shape.surfaceType;

      // Transform surface data coordinates using the transformation matrix
      if (shape.cylinderData) {
        newShape.cylinderData = this.transformCylinderData(shape.cylinderData, matrixElements);
      }
      if (shape.coneData) {
        newShape.coneData = this.transformConeData(shape.coneData, matrixElements);
      }
      if (shape.sphereData) {
        newShape.sphereData = this.transformSphereData(shape.sphereData, matrixElements);
      }
      if (shape.torusData) {
        newShape.torusData = this.transformTorusData(shape.torusData, matrixElements);
      }
    }

    return newShape;
  }

  /**
   * Transform a point using a 4x4 transformation matrix
   */
  private transformPoint(point: [number, number, number], matrix: number[]): [number, number, number] {
    const [x, y, z] = point;
    // Matrix is in column-major order (Three.js format)
    const newX = matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12];
    const newY = matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13];
    const newZ = matrix[2] * x + matrix[6] * y + matrix[10] * z + matrix[14];
    return [newX, newY, newZ];
  }

  /**
   * Transform a direction vector using a 4x4 transformation matrix (rotation only, no translation)
   */
  private transformDirection(dir: [number, number, number], matrix: number[]): [number, number, number] {
    const [x, y, z] = dir;
    // Apply only the 3x3 rotation part (no translation)
    const newX = matrix[0] * x + matrix[4] * y + matrix[8] * z;
    const newY = matrix[1] * x + matrix[5] * y + matrix[9] * z;
    const newZ = matrix[2] * x + matrix[6] * y + matrix[10] * z;
    // Normalize the result
    const len = Math.sqrt(newX * newX + newY * newY + newZ * newZ);
    if (len === 0) return [0, 0, 1];
    return [newX / len, newY / len, newZ / len];
  }

  /**
   * Transform cylinder data using the transformation matrix
   */
  private transformCylinderData(data: CADShape['cylinderData'], matrix: number[]): CADShape['cylinderData'] {
    if (!data) return undefined;
    return {
      axis: this.transformDirection(data.axis, matrix),
      location: this.transformPoint(data.location, matrix),
      radius: data.radius // Radius unchanged (assuming uniform scale or no scale)
    };
  }

  /**
   * Transform cone data using the transformation matrix
   */
  private transformConeData(data: CADShape['coneData'], matrix: number[]): CADShape['coneData'] {
    if (!data) return undefined;
    return {
      axis: this.transformDirection(data.axis, matrix),
      apex: this.transformPoint(data.apex, matrix),
      semiAngle: data.semiAngle,
      refRadius: data.refRadius
    };
  }

  /**
   * Transform sphere data using the transformation matrix
   */
  private transformSphereData(data: CADShape['sphereData'], matrix: number[]): CADShape['sphereData'] {
    if (!data) return undefined;
    return {
      center: this.transformPoint(data.center, matrix),
      radius: data.radius
    };
  }

  /**
   * Transform torus data using the transformation matrix
   */
  private transformTorusData(data: CADShape['torusData'], matrix: number[]): CADShape['torusData'] {
    if (!data) return undefined;
    return {
      axis: this.transformDirection(data.axis, matrix),
      center: this.transformPoint(data.center, matrix),
      majorRadius: data.majorRadius,
      minorRadius: data.minorRadius
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BREP SERIALIZATION (For project save/load)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Export a shape to BREP string for serialization
   * @param shape The shape to export
   * @returns BREP string representation
   */
  async exportBrep(shape: CADShape): Promise<string> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }
    if (!shape.workerShapeId) {
      throw new Error('CADKernelService: Shape must have workerShapeId for exportBrep operation');
    }

    const result = await this.workerService.exportBrep(shape.workerShapeId);
    return result.brep;
  }

  /**
   * Import a shape from BREP string
   * @param brep BREP string representation
   * @param name Optional name for the shape
   * @returns CADShape with geometry from BREP
   */
  async importBrep(brep: string, name?: string): Promise<CADShape> {
    await this.ensureWorkerReady();
    if (!this.workerService) {
      throw new Error('CADKernelService: Worker service not available');
    }

    const shapeId = this.generateId();
    const result = await this.workerService.importBrep(shapeId, brep);

    // Use the standard helper to create CADShape with proper geometry and edge handling
    return this.createCADShapeFromV2Result(result, name || 'Imported Shape');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `cad_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    this.clearSession();
    // Note: We don't unload the WASM module as it may be needed again
  }
}
